import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

function Feedback() {
  const { user } = useAuth();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Prefill from logged-in user for convenience
  useEffect(() => {
    if (user) {
      if (!username) setUsername(user.username || "");
      if (!email) setEmail(user.email || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const remaining = useMemo(() => 5000 - message.length, [message]);

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    const maxFiles = 2;
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (arr.length > maxFiles) {
      setError(`You can attach up to ${maxFiles} files.`);
      return;
    }

    const tooBig = arr.find((f) => f.size > maxSize);
    if (tooBig) {
      setError("Each file must be 2MB or less.");
      return;
    }

    setError("");
    setFiles(arr);
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    setSent(false);

    try {
      const form = new FormData();
      form.append("username", username);
      form.append("email", email);
      form.append("message", message);
      for (const f of files) form.append("attachments", f);

      const res = await fetch("/api/feedback", {
        method: "POST",
        body: form,
        credentials: "include",
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        let detail = "";
        if (Array.isArray(data?.details) && data.details.length) {
          detail = data.details[0].msg || data.details[0].message || "";
        } else if (typeof data?.details === "string") {
          detail = data.details;
        }
        throw new Error(detail || data.error || "Failed to send");
      }

      setSent(true);
      setMessage("");
      setFiles([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070c] text-white">

      {/* Page background glow */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-[-220px] right-[-160px] h-[520px] w-[520px] rounded-full bg-sky-500/6 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
      </div>

      <main className="px-3 sm:px-6 md:px-8 pt-6 pb-12">
        <div className="mx-auto max-w-6xl">
          {/* Outer framed panel (like your screenshot) */}
          <section className="rounded-[28px] border border-white/12 bg-white/4 shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_12px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl">
            {/* Header */}
            <div className="flex flex-col gap-3 border-b border-white/10 px-5 sm:px-8 py-6 sm:py-7">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/12 bg-white/5">
                  {/* sparkle icon */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2l1.2 4.2L17.4 8 13.2 9.2 12 13.4 10.8 9.2 6.6 8l4.2-1.8L12 2z"
                      className="fill-emerald-300/90"
                    />
                    <path
                      d="M19 12l.8 2.8L22.6 16 19.8 16.8 19 19.6 18.2 16.8 15.4 16l2.8-1.2L19 12z"
                      className="fill-emerald-300/70"
                    />
                    <path
                      d="M5 13l.7 2.3L8 16l-2.3.7L5 19l-.7-2.3L2 16l2.3-.7L5 13z"
                      className="fill-emerald-300/60"
                    />
                  </svg>
                </div>

                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                    Send{" "}
                    <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.15)]">
                      Feedback
                    </span>
                  </h1>
                  <p className="mt-1 text-sm sm:text-base text-white/70">
                    Found a bug, have a suggestion, or want to help improve AnimeHouse?
                    Message us here — screenshots optional.
                  </p>

                  {/* Pills (matching your “Discover / Discuss / …” vibe) */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {["Report Bug", "Suggest Feature", "UI/UX", "Other"].map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-semibold text-white/75"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notice bar (subtle, not yellow loud) */}
              <div className="rounded-2xl border border-emerald-400/18 bg-emerald-500/8 px-4 py-3 text-sm text-emerald-100/80">
                Heads up: sending can take a few minutes depending on file size.
              </div>

              {sent && (
                <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                  Thanks! Your feedback was sent.
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="px-5 sm:px-8 py-6 sm:py-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Two section cards like your screenshot */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Left: identity */}
                  <div className="lg:col-span-1 rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
                    <div className="flex items-center gap-2">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 12c2.6 0 4.7-2.1 4.7-4.7S14.6 2.6 12 2.6 7.3 4.7 7.3 7.3 9.4 12 12 12z"
                            className="fill-emerald-300/85"
                          />
                          <path
                            d="M4.4 21.4c1.8-4 4.8-6 7.6-6s5.8 2 7.6 6"
                            stroke="rgba(110,231,183,0.7)"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <h2 className="text-lg font-bold text-emerald-200">Your Info</h2>
                    </div>

                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="block text-sm text-white/65 mb-1">
                          Username
                        </label>
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                          maxLength={50}
                          autoComplete="username"
                          placeholder="Your AnimeHouse username"
                          className="w-full rounded-2xl border border-white/12 bg-white/5 px-4 py-3 text-white placeholder:text-white/35 outline-none focus:ring-2 focus:ring-emerald-400/55"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-white/65 mb-1">Email</label>
                        <input
                          type="email"
                          inputMode="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          maxLength={255}
                          autoComplete="email"
                          placeholder="you@example.com"
                          className="w-full rounded-2xl border border-white/12 bg-white/5 px-4 py-3 text-white placeholder:text-white/35 outline-none focus:ring-2 focus:ring-emerald-400/55"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: message */}
                  <div className="lg:col-span-2 rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
                    <div className="flex items-center gap-2">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M4.5 5.8A2.3 2.3 0 0 1 6.8 3.5h10.4a2.3 2.3 0 0 1 2.3 2.3v8.6a2.3 2.3 0 0 1-2.3 2.3H10l-4.2 3v-3H6.8a2.3 2.3 0 0 1-2.3-2.3V5.8z"
                            stroke="rgba(110,231,183,0.75)"
                            strokeWidth="1.6"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h2 className="text-lg font-bold text-emerald-200">Message</h2>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm text-white/65 mb-1">
                        What’s going on?
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        rows={9}
                        maxLength={5000}
                        placeholder="Describe the issue or suggestion in detail..."
                        className="w-full rounded-2xl border border-white/12 bg-white/5 px-4 py-3 text-white placeholder:text-white/35 outline-none focus:ring-2 focus:ring-emerald-400/55"
                      />
                      <div className="mt-2 flex items-center justify-between text-xs text-white/45">
                        <span>Be as specific as you can (steps, page, what you expected).</span>
                        <span>{remaining} left</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments card */}
                <div className="rounded-3xl border border-white/12 bg-white/3 p-5 sm:p-6">
                  <div className="flex items-center gap-2">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/5">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12.5 6.5l-5.7 5.7a3.2 3.2 0 0 0 4.5 4.5l6.7-6.7a2.2 2.2 0 0 0-3.1-3.1l-6.2 6.2"
                          stroke="rgba(110,231,183,0.75)"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg font-bold text-emerald-200">Attachments</h2>
                      <p className="text-sm text-white/60">
                        Add up to 2 images (max 2MB each). Screenshots help a lot.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleFiles(e.target.files)}
                      className="block w-full text-sm text-white/70 file:mr-4 file:rounded-xl file:border file:border-white/10 file:bg-white/5 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-emerald-100 hover:file:bg-white/8 cursor-pointer"
                    />

                    {!!files.length && (
                      <ul className="mt-4 space-y-2">
                        {files.map((f, idx) => (
                          <li
                            key={idx}
                            className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/4 px-4 py-3"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm text-white/85">{f.name}</p>
                              <p className="text-xs text-white/45">
                                {Math.round(f.size / 1024)} KB
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFileAt(idx)}
                              className="shrink-0 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/15"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Submit bar (like your bottom “Get in Touch” strip) */}
                <div className="rounded-3xl border border-white/12 bg-gradient-to-r from-white/4 via-white/3 to-white/4 p-5 sm:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-base font-bold text-white/90">
                        Ready to send it?
                      </p>
                      <p className="text-sm text-white/60">
                        We read these. This helps AnimeHouse get cleaner, faster, and better.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      className={[
                        "inline-flex items-center justify-center rounded-2xl px-6 py-3 font-semibold",
                        "border border-white/12 shadow-[0_10px_30px_rgba(0,0,0,0.45)]",
                        sending
                          ? "bg-emerald-700/40 text-white/70 cursor-not-allowed"
                          : "bg-emerald-400/15 hover:bg-emerald-400/22 text-emerald-100",
                        "transition",
                      ].join(" ")}
                    >
                      {sending ? "Sending..." : "Send Feedback"}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Footer line */}
            <div className="border-t border-white/10 px-6 sm:px-8 py-4 text-center text-xs text-white/35">
              Built with love for anime — and for the people who never stop talking about it.
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default Feedback;
